// ---------------------------------------------------------------------------
// WF-020 — workflow version diff primitive
//
// Registry residual gap: "Version listing + dry-run present, but UI
// integration for 'compare versions / projected outcome diff' not proven."
// The version list route (`workers/api-crm/src/routes/workflows.ts`
// GET /:id/versions) already returns the canonical {nodes, edges} payload
// per published version. What the canvas-compare UI needs next is a pure,
// tenant-agnostic primitive that takes two versions of nodes/edges and
// reports the structural delta (added / removed / changed nodes, edges).
//
// This module is intentionally pure: no DB, no env, no Hono context. It
// operates on the same {nodes, edges} shape persisted in `workflow_versions`
// and forwarded to the UI by the versions route.
// ---------------------------------------------------------------------------

export interface WorkflowVersionNode {
  id: string;
  type: string;
  actionRef: string;
  config: Record<string, unknown>;
}

export interface WorkflowVersionEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  condition?: string;
}

export interface WorkflowVersionSnapshot {
  nodes: WorkflowVersionNode[];
  edges: WorkflowVersionEdge[];
}

export interface WorkflowVersionDiff {
  nodes: {
    added: WorkflowVersionNode[];
    removed: WorkflowVersionNode[];
    changed: Array<{
      id: string;
      before: WorkflowVersionNode;
      after: WorkflowVersionNode;
      changedFields: ReadonlyArray<"type" | "actionRef" | "config">;
    }>;
  };
  edges: {
    added: WorkflowVersionEdge[];
    removed: WorkflowVersionEdge[];
    changed: Array<{
      id: string;
      before: WorkflowVersionEdge;
      after: WorkflowVersionEdge;
      changedFields: ReadonlyArray<"source" | "target" | "label" | "condition">;
    }>;
  };
  hasChanges: boolean;
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`)
    .join(",")}}`;
}

function indexById<T extends { id: string }>(items: ReadonlyArray<T>): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items) {
    map.set(item.id, item);
  }
  return map;
}

function diffNode(
  before: WorkflowVersionNode,
  after: WorkflowVersionNode,
): ReadonlyArray<"type" | "actionRef" | "config"> {
  const changed: Array<"type" | "actionRef" | "config"> = [];
  if (before.type !== after.type) changed.push("type");
  if (before.actionRef !== after.actionRef) changed.push("actionRef");
  if (stableStringify(before.config) !== stableStringify(after.config)) {
    changed.push("config");
  }
  return changed;
}

function diffEdge(
  before: WorkflowVersionEdge,
  after: WorkflowVersionEdge,
): ReadonlyArray<"source" | "target" | "label" | "condition"> {
  const changed: Array<"source" | "target" | "label" | "condition"> = [];
  if (before.source !== after.source) changed.push("source");
  if (before.target !== after.target) changed.push("target");
  if ((before.label ?? "") !== (after.label ?? "")) changed.push("label");
  if ((before.condition ?? "") !== (after.condition ?? "")) changed.push("condition");
  return changed;
}

/**
 * Compute a structural diff between two persisted workflow version
 * snapshots. Pure function — feed it the `nodes` and `edges` arrays the
 * versions list route already returns and the UI can render the delta
 * (added / removed / changed) without re-reading from the DB.
 *
 * Determinism: node/edge order does not affect the result; nodes are
 * matched by id, edges by id. `config` and `label`/`condition` use stable
 * JSON encoding so key ordering does not produce false positives.
 */
export function diffWorkflowVersions(
  before: WorkflowVersionSnapshot,
  after: WorkflowVersionSnapshot,
): WorkflowVersionDiff {
  const beforeNodes = indexById(before.nodes);
  const afterNodes = indexById(after.nodes);
  const beforeEdges = indexById(before.edges);
  const afterEdges = indexById(after.edges);

  const addedNodes: WorkflowVersionNode[] = [];
  const removedNodes: WorkflowVersionNode[] = [];
  const changedNodes: WorkflowVersionDiff["nodes"]["changed"] = [];

  for (const [id, node] of afterNodes) {
    const prior = beforeNodes.get(id);
    if (!prior) {
      addedNodes.push(node);
      continue;
    }
    const changedFields = diffNode(prior, node);
    if (changedFields.length > 0) {
      changedNodes.push({ id, before: prior, after: node, changedFields });
    }
  }
  for (const [id, node] of beforeNodes) {
    if (!afterNodes.has(id)) {
      removedNodes.push(node);
    }
  }

  const addedEdges: WorkflowVersionEdge[] = [];
  const removedEdges: WorkflowVersionEdge[] = [];
  const changedEdges: WorkflowVersionDiff["edges"]["changed"] = [];

  for (const [id, edge] of afterEdges) {
    const prior = beforeEdges.get(id);
    if (!prior) {
      addedEdges.push(edge);
      continue;
    }
    const changedFields = diffEdge(prior, edge);
    if (changedFields.length > 0) {
      changedEdges.push({ id, before: prior, after: edge, changedFields });
    }
  }
  for (const [id, edge] of beforeEdges) {
    if (!afterEdges.has(id)) {
      removedEdges.push(edge);
    }
  }

  const hasChanges =
    addedNodes.length > 0 ||
    removedNodes.length > 0 ||
    changedNodes.length > 0 ||
    addedEdges.length > 0 ||
    removedEdges.length > 0 ||
    changedEdges.length > 0;

  return {
    nodes: { added: addedNodes, removed: removedNodes, changed: changedNodes },
    edges: { added: addedEdges, removed: removedEdges, changed: changedEdges },
    hasChanges,
  };
}
